import i18n from "../i18n";
import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, UserPlus, ArrowRight, Sunrise, Sprout, Hammer, Users, ShieldCheck, Leaf } from "lucide-react";
import { resolveLoginRedirect } from "@/lib/routeHome";
import AJFarmLogo from "@/components/brand/AJFarmLogo";
import LanguageSelector from "@/components/LanguageSelector";
import { useTranslation } from "react-i18next";

export default function LoginPage() {
    const { user, login, register, error } = useAuth();
    const { t } = useTranslation();
    const loc = useLocation();
    const navigate = useNavigate();
    const passwordResetSucceeded = new URLSearchParams(loc.search).get("passwordReset") === "success";
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);

    const nextParam = new URLSearchParams(loc.search).get("next") || "";
    const redirectTo = resolveLoginRedirect(user, nextParam);
    if (user) return <Navigate to={redirectTo} replace />;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        let authedUser = null;
        if (mode === "login") authedUser = await login(email, password);
        else authedUser = await register({ email, password, name: name || email });
        setLoading(false);
        if (authedUser) {
            navigate(resolveLoginRedirect(authedUser, nextParam), { replace: true });
        }
    };

    return (
        <div className="min-h-screen w-full relative overflow-x-hidden bg-[#071b12] text-white aj-login-hero">
            {/* Ambient gradient orbs */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 aj-login-image" style={{ backgroundImage: "url('/images/aj-farm-sunrise.jpg')" }} />
                <div className="absolute inset-0 aj-login-vignette" />
                <div className="absolute left-[18%] top-[8%] w-[420px] h-[420px] rounded-full bg-[#F2C45F]/20 blur-[110px]" />
            </div>

            {/* Grain overlay */}
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
            />

            <div className="relative min-h-screen flex flex-col aj-login-stage">
                {/* Top nav bar */}
                <header className="flex items-center justify-between px-6 sm:px-12 py-6 z-10">
                    <div className="flex items-center gap-2 text-sm tracking-[0.3em] uppercase text-white/70">
                        <Sunrise className="w-4 h-4 text-aj-gold" aria-hidden="true" />
                        {t("login.built_from_field")}
                    </div>
                    <div className="flex items-center gap-3">
                        <LanguageSelector persistForUser={false} compact testId="login-language-selector" className="[&_select]:bg-[#0b281b] [&_select]:border-white/20 [&_select]:text-white" />
                        <div className="text-xs tracking-[0.25em] uppercase text-white/50 hidden sm:block">
                            {t("login.suite")}
                        </div>
                    </div>
                </header>

                {/* Center content */}
                <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-8 sm:py-10 aj-login-main">
                    <div className="w-full max-w-7xl grid lg:grid-cols-[1.12fr_0.88fr] gap-10 xl:gap-20 items-center">
                        {/* Left — approved AJ's Farm identity */}
                        <div className="text-center lg:text-left space-y-6 sm:space-y-8 aj-login-copy-panel">
                            <div className="inline-block relative aj-login-logo" data-testid="brand-logo-wrapper">
                                <div className="absolute inset-0 rounded-full bg-[#D6AA45]/15 blur-3xl scale-125" />
                                <AJFarmLogo className="relative aj-login-full-logo" />
                            </div>

                            <div>
                                <p className="text-xl sm:text-2xl text-[#F7EBCB] max-w-lg mx-auto lg:mx-0 leading-relaxed font-medium drop-shadow-lg">
                                    {t("login.hero_line_1")}<br />{t("login.hero_line_2")}
                                </p>
                            </div>

                            <div className="max-w-xl mx-auto lg:mx-0 pt-5 border-t border-[#D6AA45]/35 text-xs sm:text-sm tracking-[0.15em] text-[#EADDBB]/75 uppercase leading-relaxed font-semibold">
                                {t("login.legacy_statement")}
                            </div>

                        </div>

                        {/* Right — Glass form */}
                        <div className="block w-full aj-login-form-column">
                            <div className="relative">
                                <div className="absolute -inset-1 bg-gradient-to-br from-[#F0D783]/45 via-[#A56E22]/20 to-emerald-900/30 rounded-3xl blur-xl" />
                                <div
                                    className="relative rounded-3xl border border-[#E8C768]/25 p-7 sm:p-10 backdrop-blur-2xl shadow-2xl aj-login-card-shell"
                                    style={{ background: "linear-gradient(145deg, rgba(8,36,23,.9), rgba(13,49,32,.76))" }}
                                    data-testid="login-card"
                                >
                                    <div className="tiny-label text-aj-gold">
                                        {mode === "login" ? t("login.sign_in") : t("login.create_account")}
                                    </div>
                                    <h2 className="text-3xl font-bold mt-2">
                                        {mode === "login" ? t("login.welcome_back") : t("login.join_farm")}
                                    </h2>
                                    <p className="text-sm text-white/50 mt-2">
                                        {mode === "login"
                                            ? t("login.hero_full")
                                            : t("login.join_help")}
                                    </p>

                                    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
                                        {passwordResetSucceeded && (
                                            <div
                                                data-testid="password-reset-login-success"
                                                role="status"
                                                className="text-sm text-emerald-200 bg-emerald-500/15 border border-emerald-400/30 rounded-xl px-4 py-3"
                                            >
                                                {t("login.reset_success")}
                                            </div>
                                        )}
                                        {mode === "register" && (
                                            <div>
                                                <Label htmlFor="name" className="tiny-label text-white/70">{t("login.full_name")}</Label>
                                                <Input
                                                    id="name"
                                                    data-testid="register-name-input"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    className="h-13 mt-2 text-base bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary"
                                                    placeholder={i18n.t("final.cac7bbb6b67b")}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <Label htmlFor="email" className="tiny-label text-white/70">{t("profile.email")}</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                data-testid="login-email-input"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="h-13 mt-2 text-base bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="password" className="tiny-label text-white/70">{t("login.password")}</Label>
                                            <Input
                                                id="password"
                                                type="password"
                                                data-testid="login-password-input"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="h-13 mt-2 text-base bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-primary"
                                                required
                                            />
                                            {mode === "login" && (
                                                <div className="mt-3 text-right">
                                                    <Link
                                                        to="/forgot-password"
                                                        data-testid="login-forgot-password-link"
                                                        className="text-sm text-primary font-semibold underline underline-offset-4"
                                                    >{t("login.forgot_password")}</Link>
                                                </div>
                                            )}
                                        </div>

                                        {error && (
                                            <div
                                                data-testid="login-error"
                                                className="text-sm text-red-300 bg-red-500/15 border border-red-500/30 rounded-xl px-4 py-3"
                                            >
                                                {error}
                                            </div>
                                        )}

                                        <Button
                                            type="submit"
                                            disabled={loading}
                                            data-testid={mode === "login" ? "login-submit-btn" : "register-submit-btn"}
                                            className="w-full h-14 text-base bg-aj-gold text-[#102319] hover:bg-aj-gold-light rounded-2xl shadow-xl shadow-black/20 group font-bold"
                                        >
                                            {loading ? (
                                                <span>{t("login.signing_in")}</span>
                                            ) : mode === "login" ? (
                                                <><LogIn className="w-5 h-5 mr-2" /> {t("login.log_in")} <ArrowRight className="w-4 h-4 ml-2 opacity-70 transition-transform group-hover:translate-x-1" /></>
                                            ) : (
                                                <><UserPlus className="w-5 h-5 mr-2" /> {t("login.create_account")}</>
                                            )}
                                        </Button>

                                        <div className="text-center text-sm text-white/60">
                                            {mode === "login" ? (
                                                <>{t("login.new_here")} {" "}
                                                    <button
                                                        type="button"
                                                        className="text-primary font-semibold underline underline-offset-4"
                                                        data-testid="switch-to-register-btn"
                                                        onClick={() => setMode("register")}
                                                    >{t("login.sign_up")}</button>
                                                </>
                                            ) : (
                                                <>{t("login.have_account")} {" "}
                                                    <button
                                                        type="button"
                                                        className="text-primary font-semibold underline underline-offset-4"
                                                        data-testid="switch-to-login-btn"
                                                        onClick={() => setMode("login")}
                                                    >{t("login.sign_in")}</button>
                                                </>
                                            )}
                                        </div>
                                    </form>

                                    <div className="mt-7 pt-5 border-t border-[#E8C768]/20 text-center uppercase leading-relaxed">
                                        <span className="block text-[9px] sm:text-[10px] tracking-[0.2em] text-white/45">{t("login.designed_in_field")}</span>
                                        <span className="aj-brand-signature block mt-1 text-2xl sm:text-3xl text-[#E5BE62]">{i18n.t("final.044745750a25")}</span>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <LoginLegacyBanner />
        </div>
    );
}

const LOGIN_VALUES = [
    [Sunrise, "WE PLAN TODAY", "Every plan brings us closer."],
    [Hammer, "WE BUILD STRONG", "With sacrifice, we create lasting value."],
    [Users, "WE GROW TOGETHER", "With patience, we see it flourish."],
    [ShieldCheck, "WE PROTECT & IMPROVE", "We protect what we build and make it better every day."],
    [Leaf, "WE CONTINUE THE LEGACY", "For our child, for the next generation."],
];

function LoginLegacyBanner() {
    return (
        <section className="relative bg-[#06170f] border-t border-[#D6AA45]/25 px-5 sm:px-8 lg:px-12 aj-login-legacy" data-testid="login-legacy-banner">
            <div className="absolute inset-0 aj-login-banner-glow pointer-events-none" />
            <div className="relative max-w-7xl mx-auto">
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 aj-login-values-grid">
                    {LOGIN_VALUES.map(([Icon, title, description]) => (
                        <article key={title} className="aj-login-value-card">
                            <Icon className="w-5 h-5 text-aj-gold shrink-0" aria-hidden="true" />
                            <div>
                                <h2 className="text-[10px] sm:text-xs font-bold tracking-[0.14em] text-[#F4E5BA]">{title}</h2>
                                <p className="mt-1.5 text-xs leading-relaxed text-white/50">{description}</p>
                            </div>
                        </article>
                    ))}
                </div>
                <div className="mt-8 pt-8 border-t border-[#D6AA45]/20 grid lg:grid-cols-[1fr_auto] gap-6 items-end aj-login-legacy-message">
                    <div>
                        <div className="flex items-center gap-2 text-aj-gold text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.28em] font-bold">
                            <Sprout className="w-5 h-5 shrink-0" aria-hidden="true" />
                            <span>{i18n.t("final.e6469a65672a")}</span>
                        </div>
                        <p className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-[#F7EBCB] aj-login-legacy-future">
                            {i18n.t("final.9338d7d6f677")} <span className="lg:inline block">{i18n.t("final.38eb50e0cbea")}</span>
                        </p>
                    </div>
                    <div className="text-[10px] sm:text-xs tracking-[0.2em] text-white/55 font-semibold uppercase">{i18n.t("final.cf86f160deef")}</div>
                </div>
            </div>
        </section>
    );
}
